import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';
import { TaskHelpers } from 'ontraccr-common';

import Debouncer from '../../../../helpers/Debouncer';
import { getLinkId } from '../../../formHelpers';
import { loadManualEntryData, loadTasks } from '../../../../clock/ManualEntry/manualEntryHelpers';
import { buildTimeTrackingGridFromTasks } from './TimeTrackingGridFromTasks';

const createInitialLastPreloadSnapshot = () => ({
  projectId: undefined,
  linkedDateValue: null,
  costcodeId: undefined,
  hasPreloaded: false,
  gridDate: null,
  preloadFixedDateIso: null,
});

function taskPreloadFailsHardRequirements({
  preloadExistingEntries,
  preloadProject,
  linkedProjectId,
  preloadStatuses,
  preloadDateField,
  linkedDateValue,
  preloadCostcodeDropdown,
  linkedCostcodeId,
}) {
  if (!preloadExistingEntries) return true;
  if (!(preloadProject || linkedProjectId) || !preloadStatuses?.length) return true;
  if (preloadDateField && !linkedDateValue) return true;
  if (preloadCostcodeDropdown && !linkedCostcodeId) return true;
  return false;
}

function computeTaskPreloadSkipFlags({
  lastSnapshot,
  linkedDateValue,
  preloadDateField,
  preloadTimeEntriesFixedDate,
  linkedProjectId,
  preloadProject,
  linkedCostcodeId,
  preloadCostcodeDropdown,
  currentGridDate,
}) {
  const {
    projectId: lastProjectId,
    linkedDateValue: lastDateValue,
    costcodeId: lastCostcodeId,
    hasPreloaded,
    gridDate: lastPreloadGridDate,
    preloadFixedDateIso: lastPreloadFixedDateIso,
  } = lastSnapshot;

  const hasAlreadyPreloaded = Boolean(preloadProject && hasPreloaded);
  const hasSameProject = Boolean(linkedProjectId && linkedProjectId === lastProjectId);
  const hasSameCostcode = !preloadCostcodeDropdown
    || linkedCostcodeId === lastCostcodeId;

  const { startTime: linkedStart, endTime: linkedEnd } = linkedDateValue ?? {};
  const { startTime: lastStart, endTime: lastEnd } = lastDateValue ?? {};

  const hasSameLinkedDate = !preloadDateField || (
    Boolean(linkedDateValue)
    && linkedStart?.toMillis() === lastStart?.toMillis()
    && linkedEnd?.toMillis() === lastEnd?.toMillis()
  );

  const hasSamePreloadFixedDate = Boolean(
    preloadTimeEntriesFixedDate && preloadTimeEntriesFixedDate === lastPreloadFixedDateIso,
  );

  const gridDateChanged = hasPreloaded && currentGridDate !== lastPreloadGridDate;

  return {
    hasAlreadyPreloaded,
    hasSameProject,
    hasSameCostcode,
    hasSameLinkedDate,
    hasSamePreloadFixedDate,
    gridDateChanged,
  };
}

function skipTaskPreloadNoLinkedDateMode(currentGridDate, flags) {
  const {
    gridDateChanged,
    hasSameProject,
    hasAlreadyPreloaded,
    hasSameCostcode,
  } = flags;
  if (currentGridDate === null) return true;
  if (!gridDateChanged && (hasSameProject || hasAlreadyPreloaded) && hasSameCostcode) {
    return true;
  }
  return false;
}

function skipTaskPreloadStableGridDate(flags, preloadDateField, preloadTimeEntriesFixedDate) {
  const {
    gridDateChanged,
    hasSameLinkedDate,
    hasSamePreloadFixedDate,
    hasSameProject,
    hasAlreadyPreloaded,
    hasSameCostcode,
  } = flags;
  if (gridDateChanged) return false;
  const projectAndCostcodeOk = (hasSameProject || hasAlreadyPreloaded) && hasSameCostcode;
  if (!projectAndCostcodeOk) return false;
  if (preloadDateField) return hasSameLinkedDate;
  if (preloadTimeEntriesFixedDate) return hasSamePreloadFixedDate;
  return false;
}

/**
 * When !forceReload, returns true if we should skip running preload (no fetch).
 */
function shouldSkipPreload({
  noPreloadDate,
  preloadDateField,
  preloadTimeEntriesFixedDate,
  preloadExistingEntries,
  preloadProject,
  linkedProjectId,
  preloadStatuses,
  linkedDateValue,
  preloadCostcodeDropdown,
  linkedCostcodeId,
  lastSnapshot,
  currentGridDate,
}) {
  if (taskPreloadFailsHardRequirements({
    preloadExistingEntries,
    preloadProject,
    linkedProjectId,
    preloadStatuses,
    preloadDateField,
    linkedDateValue,
    preloadCostcodeDropdown,
    linkedCostcodeId,
  })) {
    return true;
  }

  const flags = computeTaskPreloadSkipFlags({
    lastSnapshot,
    linkedDateValue,
    preloadDateField,
    preloadTimeEntriesFixedDate,
    linkedProjectId,
    preloadProject,
    linkedCostcodeId,
    preloadCostcodeDropdown,
    currentGridDate,
  });

  if (noPreloadDate) {
    return skipTaskPreloadNoLinkedDateMode(currentGridDate, flags);
  }

  return skipTaskPreloadStableGridDate(
    flags,
    preloadDateField,
    preloadTimeEntriesFixedDate,
  );
}

const getProjectsTasksForGrid = async ({
  preloadDateField,
  preloadDateModifier,
  preloadDateCount,
  preloadTimeEntriesFixedDate,
  gridDateIso,
  linkedStart,
  linkedEnd,
  linkedProjectId,
  costcodeName,
  costcodeCode,
}) => {
  try {
    const now = DateTime.local();
    let startTimeToFilter;
    let endTimeToFilter;
    if (gridDateIso) {
      const d = DateTime.fromISO(gridDateIso);
      if (d.isValid) {
        startTimeToFilter = d.startOf('day');
        endTimeToFilter = d.endOf('day');
      }
    } else if (preloadDateField && linkedStart && linkedEnd) {
      startTimeToFilter = linkedStart.startOf('day');
      endTimeToFilter = linkedEnd.endOf('day');
    } else if (preloadTimeEntriesFixedDate) {
      const d = DateTime.fromISO(preloadTimeEntriesFixedDate);
      if (d.isValid) {
        startTimeToFilter = d.startOf('day');
        endTimeToFilter = d.endOf('day');
      }
    }
    if (!startTimeToFilter || !endTimeToFilter) {
      startTimeToFilter = now.startOf('day').minus({ [preloadDateModifier]: preloadDateCount })
        .plus({ days: 1 });
      endTimeToFilter = now.endOf('day');
    }

    const params = {
      projectId: linkedProjectId,
      startDate: startTimeToFilter.toISODate(),
      endDate: endTimeToFilter.toISODate(),
    };

    if (costcodeName && costcodeCode) {
      params.costcodeName = costcodeName;
      params.costcodeCode = costcodeCode;
    }

    return await loadTasks(params);
  } catch (err) {
    Sentry.captureException(err);
    return null;
  }
};

function pickStoredGridDateIso(currentGridDate) {
  if (!currentGridDate) return null;
  return DateTime.fromISO(currentGridDate).isValid ? currentGridDate : null;
}

function resolveGridDateIso({
  forceReload,
  preloadDateField,
  preloadTimeEntriesFixedDate,
  linkedDateValue,
  lastPreloadSnapshot,
  currentGridDate,
}) {
  const linkedDateIsoForGrid = linkedDateValue?.startTime?.toISODate?.() ?? null;
  const lastDv = lastPreloadSnapshot.linkedDateValue;
  const linkedStartMs = linkedDateValue?.startTime?.toMillis() ?? null;
  const linkedEndMs = linkedDateValue?.endTime?.toMillis() ?? null;
  const lastStartMs = lastDv?.startTime?.toMillis() ?? null;
  const lastEndMs = lastDv?.endTime?.toMillis() ?? null;
  const linkedFieldDateChanged = linkedStartMs !== lastStartMs || linkedEndMs !== lastEndMs;

  const shouldSyncGridDateToLinkedField = Boolean(
    (preloadDateField && linkedDateIsoForGrid && linkedFieldDateChanged)
    || (forceReload && currentGridDate !== linkedDateIsoForGrid),
  );
  const shouldSyncGridDateToFixedPreloadDate = Boolean(
    preloadTimeEntriesFixedDate
    && (forceReload || !lastPreloadSnapshot.hasPreloaded),
  );
  if (shouldSyncGridDateToFixedPreloadDate) return preloadTimeEntriesFixedDate;
  if (shouldSyncGridDateToLinkedField) return linkedDateIsoForGrid;
  return pickStoredGridDateIso(currentGridDate);
}

function deriveFilterRangeForTasks(gridDateIso, linkedStartDate, linkedEndDate, preloadDateField) {
  const base = {
    filterLinkedStart: linkedStartDate,
    filterLinkedEnd: linkedEndDate,
    useLinkedRangeForTaskFilter: Boolean(preloadDateField),
  };
  if (!gridDateIso) return base;
  const d = DateTime.fromISO(gridDateIso);
  if (!d.isValid) return base;
  return {
    filterLinkedStart: d.startOf('day'),
    filterLinkedEnd: d.endOf('day'),
    useLinkedRangeForTaskFilter: true,
  };
}

async function loadTaskCustomDataMap(taskIds, loadData) {
  if (!taskIds.length) return {};
  try {
    const { dataMap = {} } = await loadData(taskIds);
    return dataMap;
  } catch (err) {
    Sentry.captureException(err);
    return {};
  }
}

async function fetchTasksAndBuildMergedGrid({
  gridDateIso,
  filterLinkedStart,
  filterLinkedEnd,
  useLinkedRangeForTaskFilter,
  linkedStartDate,
  linkedEndDate,
  preloadProject,
  linkedProjectId,
  preloadDateField,
  preloadDateModifier,
  preloadDateCount,
  preloadTimeEntriesFixedDate,
  preloadStatuses,
  linkedCostcodeName,
  linkedCostcodeCode,
  forceReload,
  id,
  templateColumns,
  divisionUserMap,
  divisionCostcodeMap,
  divisionEquipmentMap,
  projectIdMap,
  phaseIdMap,
  responsesRef,
  loadManualEntryDataForTasks,
}) {
  const projectTasks = await getProjectsTasksForGrid({
    preloadDateField,
    preloadDateModifier,
    preloadDateCount,
    preloadTimeEntriesFixedDate,
    gridDateIso,
    linkedStart: linkedStartDate,
    linkedEnd: linkedEndDate,
    linkedProjectId: preloadProject ?? linkedProjectId,
    costcodeName: linkedCostcodeName,
    costcodeCode: linkedCostcodeCode,
  });

  const ourTasks = TaskHelpers.getPreloadedEntries({
    preloadDateCount,
    preloadDateModifier,
    preloadStatuses,
    preloadProject,
    preloadDateField: useLinkedRangeForTaskFilter,
    linkedProjectId,
    linkedStart: filterLinkedStart,
    linkedEnd: filterLinkedEnd,
    timeEntryUserMap: { tasks: projectTasks ?? [] },
  });

  const taskIds = ourTasks.map((t) => t.id).filter(Boolean);
  const taskCustomDataMap = await loadTaskCustomDataMap(taskIds, loadManualEntryDataForTasks);

  const prev = responsesRef.current?.[id] ?? {};
  const keptColumns = forceReload ? [] : (prev.columns ?? templateColumns ?? []);
  const keptValues = forceReload ? [] : (prev.values ?? []).filter((v) => !v.isPreloaded);

  return buildTimeTrackingGridFromTasks({
    tasks: ourTasks,
    gridDateIso,
    divisionUserMap,
    divisionCostcodeMap,
    divisionEquipmentMap,
    taskCustomDataMap,
    projectIdMap,
    phaseIdMap,
    existingColumns: keptColumns,
    existingValues: keptValues,
  });
}

export default function useTimeTrackingGridTaskPreload({
  responses,
  id,
  setResponses,
  responding,
  configProps,
  setLoading,
  divisionUserMap,
  divisionCostcodeMap,
  divisionEquipmentMap,
  projectIdMap = {},
  phaseIdMap = {},
  columns: templateColumns = [],
}) {
  const {
    preloadExistingEntries,
    preloadProject,
    preloadDropdown,
    preloadStatuses = [],
    preloadDateField,
    preloadDateModifier = 'days',
    preloadDateCount = 1,
    preloadTimeEntriesFixedDate,
    preloadCostcodeDropdown,
    preloadCostcodeName,
    preloadCostcodeCode,
    timeTrackingGridPreloadSource,
  } = configProps ?? {};

  const [debouncer] = useState(new Debouncer());

  const [lastPreloadSnapshot, setLastPreloadSnapshot] = useState(createInitialLastPreloadSnapshot);

  const responsesRef = useRef(responses);
  responsesRef.current = responses;

  const enabled = timeTrackingGridPreloadSource === 'timeEntries';

  useEffect(() => {
    if (!enabled) {
      setLastPreloadSnapshot(createInitialLastPreloadSnapshot());
    }
  }, [enabled]);

  const linkedProjectId = useMemo(() => (
    getLinkId({ responses, defaultValue: null, linkField: preloadDropdown }) ?? null
  ), [responses, preloadDropdown]);

  const linkedCostcodeId = useMemo(() => {
    if (!preloadCostcodeDropdown) return null;
    const ourCostcodeId = getLinkId({
      responses,
      defaultValue: null,
      linkField: preloadCostcodeDropdown,
    }) ?? null;
    if (!ourCostcodeId) return null;
    const splitCostcodeId = ourCostcodeId?.split('.');
    if (splitCostcodeId?.length === 2) {
      return splitCostcodeId[1];
    }
    return ourCostcodeId;
  }, [responses, preloadCostcodeDropdown]);

  const linkedCostcodeName = useMemo(() => {
    if (preloadCostcodeName) return preloadCostcodeName;
    if (!linkedCostcodeId || !divisionCostcodeMap[linkedCostcodeId]) return null;
    return divisionCostcodeMap[linkedCostcodeId]?.name ?? null;
  }, [preloadCostcodeName, linkedCostcodeId, divisionCostcodeMap]);

  const linkedCostcodeCode = useMemo(() => {
    if (preloadCostcodeCode) return preloadCostcodeCode;
    if (!linkedCostcodeId || !divisionCostcodeMap[linkedCostcodeId]) return null;
    return divisionCostcodeMap[linkedCostcodeId]?.code ?? null;
  }, [preloadCostcodeCode, linkedCostcodeId, divisionCostcodeMap]);

  const linkedDateValue = useMemo(() => {
    if (!preloadDateField) return null;
    const {
      [preloadDateField]: dateResponse = {},
    } = responses ?? {};

    const { startTime, endTime, date } = dateResponse;

    if (date) {
      return {
        date: DateTime.fromMillis(date).toISODate(),
        startTime: DateTime.fromMillis(date).startOf('day'),
        endTime: DateTime.fromMillis(date).endOf('day'),
      };
    }

    if (startTime && endTime) {
      return {
        startTime: DateTime.fromMillis(startTime).startOf('day'),
        endTime: DateTime.fromMillis(endTime).endOf('day'),
      };
    }

    return null;
  }, [responses, preloadDateField]);

  const preloadFromTasks = useCallback(async (forceReload = false) => {
    if (!enabled) return;

    const snap = responsesRef.current ?? {};
    const currentValues = snap?.[id]?.values ?? [];
    const hasNonPreloadedEntries = currentValues.some((value) => !value.isPreloaded);
    const linkedStartDate = linkedDateValue?.startTime || null;
    const linkedEndDate = linkedDateValue?.endTime || null;
    const noPreloadDate = !preloadDateField && !preloadTimeEntriesFixedDate;
    const currentGridDate = snap?.[id]?.date ?? null;

    if (!forceReload && hasNonPreloadedEntries) return;
    if (!responding) return;

    if (forceReload && noPreloadDate) {
      return;
    }

    if (!forceReload && currentGridDate === null && noPreloadDate) {
      setLastPreloadSnapshot((p) => ({ ...p, gridDate: null }));
      return;
    }

    if (!forceReload && shouldSkipPreload({
      noPreloadDate,
      preloadDateField,
      preloadTimeEntriesFixedDate,
      preloadExistingEntries,
      preloadProject,
      linkedProjectId,
      preloadStatuses,
      linkedDateValue,
      preloadCostcodeDropdown,
      linkedCostcodeId,
      lastSnapshot: lastPreloadSnapshot,
      currentGridDate,
    })) {
      return;
    }

    if (typeof setLoading === 'function') setLoading(true);

    try {
      const gridDateIso = resolveGridDateIso({
        forceReload,
        preloadDateField,
        preloadTimeEntriesFixedDate,
        linkedDateValue,
        lastPreloadSnapshot,
        currentGridDate,
      });
      const {
        filterLinkedStart,
        filterLinkedEnd,
        useLinkedRangeForTaskFilter,
      } = deriveFilterRangeForTasks(
        gridDateIso,
        linkedStartDate,
        linkedEndDate,
        preloadDateField,
      );

      const { columns: mergedColumns, values: mergedValues } = await fetchTasksAndBuildMergedGrid({
        gridDateIso,
        filterLinkedStart,
        filterLinkedEnd,
        useLinkedRangeForTaskFilter,
        linkedStartDate,
        linkedEndDate,
        preloadProject,
        linkedProjectId,
        preloadDateField,
        preloadDateModifier,
        preloadDateCount,
        preloadTimeEntriesFixedDate,
        preloadStatuses,
        linkedCostcodeName,
        linkedCostcodeCode,
        forceReload,
        id,
        templateColumns,
        divisionUserMap,
        divisionCostcodeMap,
        divisionEquipmentMap,
        projectIdMap,
        phaseIdMap,
        responsesRef,
        loadManualEntryDataForTasks: (taskIds) => loadManualEntryData(taskIds, false),
      });

      const timezone = DateTime.local().zoneName;

      setResponses((p) => ({
        ...p,
        [id]: {
          ...p[id],
          values: mergedValues,
          columns: mergedColumns?.length ? mergedColumns : (templateColumns ?? []),
          dataType: 'TimeTrackingGrid',
          date: gridDateIso,
          timezone: p?.[id]?.timezone ?? timezone,
        },
      }));

      setLastPreloadSnapshot({
        projectId: linkedProjectId,
        linkedDateValue,
        costcodeId: linkedCostcodeId,
        hasPreloaded: true,
        gridDate: gridDateIso,
        preloadFixedDateIso: preloadTimeEntriesFixedDate,
      });
    } finally {
      if (typeof setLoading === 'function') setLoading(false);
    }
  }, [
    enabled,
    id,
    lastPreloadSnapshot,
    linkedCostcodeCode,
    linkedCostcodeId,
    linkedCostcodeName,
    linkedDateValue,
    linkedProjectId,
    preloadCostcodeDropdown,
    preloadDateCount,
    preloadDateField,
    preloadDateModifier,
    preloadTimeEntriesFixedDate,
    preloadExistingEntries,
    preloadProject,
    preloadStatuses,
    responding,
    setLoading,
    setResponses,
    templateColumns,
    divisionUserMap,
    divisionCostcodeMap,
    divisionEquipmentMap,
    projectIdMap,
    phaseIdMap,
  ]);

  useEffect(() => {
    debouncer.debounce(() => preloadFromTasks(false), 500);
  }, [
    configProps,
    id,
    linkedProjectId,
    linkedDateValue?.startTime,
    linkedDateValue?.endTime,
    linkedCostcodeId,
    linkedCostcodeName,
    linkedCostcodeCode,
    lastPreloadSnapshot.projectId,
    lastPreloadSnapshot.costcodeId,
    lastPreloadSnapshot.hasPreloaded,
    preloadFromTasks,
    responses,
    debouncer,
  ]);

  return {
    triggerTaskPreload: () => preloadFromTasks(true),
  };
}
